import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  MapPin,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const LGC_BESTSELLER_SLUGS = [
  "carton-grand-volume-renforce",
  "carton-penderie-grand",
  "pack-cartons-demenagement-t2",
  "papier-bulle-10m",
  "rouleau-adhesif",
  "chariot-demenagement",
];

const Sections = ({ city }) => {
  const [bestsellers, setBestsellers] = useState([]);

  useEffect(() => {
    fetchProducts()
      .then((res) => {
        const all = res || [];
        const picked = LGC_BESTSELLER_SLUGS
          .map((s) => all.find((p) => p.slug === s))
          .filter(Boolean);
        setBestsellers(picked);
      })
      .catch(() => setBestsellers([]));
  }, []);

  return (
    <>
      {/* ======= SECTION 2 — Livraison à La Garenne-Colombes + 2 engagements ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison directe
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de vos cartons de déménagement à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pas besoin de vous déplacer en magasin de bricolage ni de
              surcharger le coffre de votre voiture. Nous livrons toutes vos
              fournitures directement chez vous ou à votre bureau.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Livraison par notre propre société",
                body: "Nous n'utilisons aucun transporteur externe. Notre équipe prend soin de vos colis du chargement jusqu'à votre adresse en Île-de-France.",
                Icon: Truck,
              },
              {
                title: "Matériel impeccable",
                body: "Nos cartons sont transportés à l'abri de la pluie et de l'humidité. Vous recevez des emballages propres, secs et prêts à être scotchés.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Qualité pro à prix compétitifs ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Tag className="h-3.5 w-3.5" strokeWidth={2.6} />
              Meilleur rapport qualité-prix
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Des cartons de qualité professionnelle à des prix très
              compétitifs
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous prouvons qu&apos;il est possible d&apos;acheter du matériel
              de déménagement de haute qualité sans vous ruiner. Notre
              priorité est de vous offrir le meilleur rapport qualité-prix.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Tarifs dégressifs",
                body: "Plus vous achetez de cartons pour votre logement, plus le prix unitaire baisse. C'est idéal pour faire des économies sur le budget global.",
                Icon: Tag,
              },
              {
                title: "Solidité renforcée",
                body: "Nos cartons sont beaucoup plus épais que ceux des grandes surfaces. Ils résistent à l'écrasement et vous pouvez les empiler les uns sur les autres dans le camion sans aucun risque.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#FF7A00] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Meilleures ventes à La Garenne-Colombes ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#FF7A00" }}>
              Top ventes locales
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Les meilleures ventes de cartons à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Découvrez les produits préférés de nos clients dans votre ville
              pour emballer efficacement toutes vos affaires.
            </p>
          </div>

          {bestsellers.length === 0 ? (
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="aspect-square bg-white animate-pulse rounded-2xl border border-[#cfdbe6]"
                />
              ))}
            </div>
          ) : (
            <div
              className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
              data-testid="city-lgc-bestsellers"
            >
              {bestsellers.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/cartons-demenagement"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-lgc-cta-shop"
            >
              Voir tout le catalogue
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/packs-cartons"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="city-lgc-cta-kits"
            >
              <Layers className="h-4 w-4" strokeWidth={2.6} />
              Choisir un kit
            </Link>
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-lgc-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
